import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from "react-redux"
import { hasUserPermission } from "../utils/permissionHelper";
import { selectUser } from "../redux/userSlice";
import { selectAuth } from "../redux/authSlice";
import LayoutSidebar from "../components/LayoutSidebar";
import { useRequest } from "../hooks/UseRequest";
import Loading from "../components/Loading";
import { setModal } from "../redux/modalSlice";

const DevToolbox = () => {
  const [errorReTotalBulk, setErrorReTotalBulk] = useState('');
  const [responses, setResponses] = useState({});
  const [loading, setIsLoading] = useState({});
  const [featureFlags, setFeatureFlags] = useState([]);
  const [dsBypassScenarios, setDsBypassScenarios] = useState([]);
  const [loanAgreementForSigning, setLoanAgreementForSigning] = useState('');
  const [loanForReset, setLoanForReset] = useState('');
  const [loanForRecalc, setLoanForRecalc] = useState('');
  const [loanForRetotal, setLoanForRetotal] = useState('');
  const [loanForStatusChange, setLoanForStatusChange] = useState('');
  const [loansForStatusChangeBulk, setLoansForStatusChangeBulk] = useState([]);
  const [paymentForStatusChange, setPaymentForStatusChange] = useState('');
  const [loansForReTotalBulk, setLoansForReTotalBulk] = useState('');
  const [error, setError] = useState('');

  const [loanForAdjustmentPayment, setLoanForAdjustmentPayment] = useState(0);
  const [adjustmentPrincipal, setAdjustmentPrincipal] = useState(0);
  const [adjustmentInterest, setAdjustmentInterest] = useState(0);
  const [adjustmentLateFee, setAdjustmentLateFee] = useState(0);
  const [adjustmentNsfFee, setAdjustmentNsfFee] = useState(0);
  const [adjustmentPrediscountPrincipal, setAdjustmentPrediscountPrincipal] = useState(0);
  const [adjustmentPrediscountInterest, setAdjustmentPrediscountInterest] = useState(0);
  const [adjustmentPrediscountLateFee, setAdjustmentPrediscountLateFee] = useState(0);
  const [adjustmentPrediscountNsfFee, setAdjustmentPrediscountNsfFee] = useState(0);

  const navi = useNavigate()
  const user = useSelector(selectUser)
  const auth = useSelector(selectAuth)
  const requests = useRequest()
  const dispatch = useDispatch()

  useEffect(() => {
    if (user && user.data.user_permission_options) {
      const activePermissions = user.spoofed_user.user_permission_options ? user.spoofed_user.user_permission_options : user.data.user_permission_options
      if (!hasUserPermission(activePermissions, `is_dev`)) {
        navi('/403')
      }
    }
  }, [user])

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT !== 'production' && auth.accessToken) {
      const fetchFeatureFlags = async () => {
        try {
          setIsLoading((prev) => ({ ...prev, feature_flags: true }));
          requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/feature_flags`);
          const response = await requests.get();
          if (response.status === 200) {
            setFeatureFlags(response.data?.data || []);
          }
        } catch (error) {
          setResponses((prev) => ({ ...prev, feature_flags_error: error?.message }));
        } finally {
          setIsLoading((prev) => ({ ...prev, feature_flags: false }));
        }
      };
      fetchFeatureFlags();
      fetchDsBypassScenarios();
    }
  }, [auth.accessToken])

  const handleToggleFeatureFlag = async (featureClass, newState, scope) => {
    const loadingKey = `toggle_${scope}_${featureClass}`;
    try {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: true }));
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/feature_flags/toggle`);
      const response = await requests.post({ feature: featureClass, is_active: newState, scope });
      if (response.status === 200) {
        const updatedFlag = response.data?.data;
        setFeatureFlags((prev) => prev.map((flag) => (
          flag.class === updatedFlag.class ? updatedFlag : flag
        )));
      }
    } catch (error) {
      setResponses((prev) => ({ ...prev, [`${loadingKey}_error`]: error?.message }));
    } finally {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }

  const fetchDsBypassScenarios = async () => {
    try {
      setIsLoading((prev) => ({ ...prev, ds_bypass_scenarios: true }));
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/ds_bypass/scenarios`);
      const response = await requests.get();
      if (response.status === 200) {
        setDsBypassScenarios(response.data?.data || []);
      }
    } catch (error) {
      setResponses((prev) => ({ ...prev, ds_bypass_scenarios_error: error?.message }));
    } finally {
      setIsLoading((prev) => ({ ...prev, ds_bypass_scenarios: false }));
    }
  }

  const handleActivateDsScenario = async (endpoint, scenarioName) => {
    const loadingKey = `ds_scenario_${endpoint}`;
    try {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: true }));
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/ds_bypass/scenarios/activate`);
      const response = await requests.post({ endpoint, scenario_name: scenarioName });
      if (response.status === 200) {
        setDsBypassScenarios((prev) => prev.map((row) => (
          row.endpoint === endpoint
            ? {
              ...row,
              active_scenario: scenarioName,
              scenarios: row.scenarios.map((s) => ({ ...s, is_default: s.scenario_name === scenarioName })),
            }
            : row
        )));
      }
    } catch (error) {
      setResponses((prev) => ({ ...prev, [`${loadingKey}_error`]: error?.message }));
    } finally {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }

  const handleClearUserOverride = async (featureClass) => {
    const loadingKey = `clear_${featureClass}`;
    try {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: true }));
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/feature_flags/clear_user_override`);
      const response = await requests.post({ feature: featureClass });
      if (response.status === 200) {
        const updatedFlag = response.data?.data;
        setFeatureFlags((prev) => prev.map((flag) => (
          flag.class === updatedFlag.class ? updatedFlag : flag
        )));
      }
    } catch (error) {
      setResponses((prev) => ({ ...prev, [`${loadingKey}_error`]: error?.message }));
    } finally {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }

  const handleCacheClear = async () => {
    try {
      setIsLoading({ ...loading, cache: true });
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/clear_cache`)
      const response = await requests.post({});
      if (response.status === 200) {
        setIsLoading({ ...loading, cache: false })
        setResponses({ ...responses, cache_success: true, cache_error: null })
      }
    } catch (error) {
      setIsLoading({ ...loading, cache: false })
      setResponses({ ...responses, cache_error: error?.message, cache_success: null })
    }
  }
  const handleConfigClear = async () => {
    try {
      setIsLoading({ ...loading, config: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/clear_config`)
      const response = await requests.post({});
      if (response.status === 200) {
        setIsLoading({ ...loading, config: false })
        setResponses({ ...responses, config_success: true, config_error: null })
      }
    } catch (error) {
      setIsLoading({ ...loading, config: false })
      setResponses({ ...responses, config_error: error?.message, config_success: null })
    }
  }

  const handleLeadStatusPendingApprovalCron = async () => {
    try {
      setIsLoading({ ...loading, lead_status_pending_cron: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/cron_lead_status_pending_approval`)
      const response = await requests.post({});
      if (response.status === 200) {
        setIsLoading({ ...loading, lead_status_pending_cron: false })
        setResponses({ ...responses, lead_status_pending_cron_success: true, lead_status_pending_cron_error: null })
      }
    } catch (error) {
      setIsLoading({ ...loading, lead_status_pending_cron: false })
      setResponses({ ...responses, lead_status_pending_cron_error: error?.message, lead_status_pending_cron_success: null })
    }
  }

  const handleSignLoanAsCustomer = async () => {
    try {
      setIsLoading({ ...loading, sign_loan_agreement: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/sign_loan_agreement_as_customer`)
      const response = await requests.post({ lead_id: loanAgreementForSigning });
      if (response.status === 200) {
        setIsLoading({ ...loading, sign_loan_agreement: false })
        setResponses({
          ...responses,
          sign_loan_agreement_success: true,
          sign_loan_agreement_error: null,
          sign_loan_agreement_message: response.data?.message || 'Loan signing completed successfully'
        })
        dispatch(
          setModal({
            is_active: true,
            title: 'Loan Agreement Signed',
            content: response.data.message,
            type: 'test_data_modal',
            options: {
              customer_id: response.data?.customer_id,
              customer_name: response.data?.customer_name,
            }
          })
        )
      }
    } catch (error) {
      setIsLoading({ ...loading, sign_loan_agreement: false })
      setResponses({ ...responses, sign_loan_agreement_error: error?.response?.data?.message || error?.message, sign_loan_agreement_success: null })
    }
  }

  const handleResetLoan = async () => {
    try {
      setIsLoading({ ...loading, reset_loan: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/reset_loan_to_original_state`)
      const response = await requests.post({ loan_id: loanForReset });
      if (response.status === 200) {
        setIsLoading({ ...loading, reset_loan: false })
        setResponses({
          ...responses,
          reset_loan_success: true,
          reset_loan_error: null,
          reset_loan_message: response.data?.message || 'Loan reset completed successfully',
          reset_loan_details: response.data?.reset_details
        })
        dispatch(
          setModal({
            is_active: true,
            title: 'Loan reset completed successfully',
            content: response.data.message,
            type: 'test_data_modal',
            options: {
              customer_id: response.data?.customer_id,
              customer_name: response.data?.customer_name,
            }
          })
        )
      }
    } catch (error) {
      setIsLoading({ ...loading, reset_loan: false })
      setResponses({ ...responses, reset_loan_error: error?.response?.data?.message || error?.message, reset_loan_success: null })
    }
  }

  const handleForceRecalculation = async () => {
    try {
      setIsLoading({ ...loading, force_recalc: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/force_recalculation`)
      const response = await requests.post({ loan_id: loanForRecalc });
      if (response.status === 200) {
        setIsLoading({ ...loading, force_recalc: false })
        setResponses({ ...responses, force_recalc_success: true, force_recalc_error: null })
      }
    } catch (error) {
      setIsLoading({ ...loading, force_recalc: false })
      if (error?.response?.status === 404) {
        setResponses({ ...responses, force_recalc_error: 'Loan ID not found', force_recalc_success: null })
      } else {
        setResponses({ ...responses, force_recalc_error: error?.message, force_recalc_success: null })
      }
    }
  }
  const handleForceRetotal = async () => {
    try {
      setIsLoading({ ...loading, force_retotal: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/force_retotal`)
      const response = await requests.post({ loan_id: loanForRetotal });
      if (response.status === 200) {
        setIsLoading({ ...loading, force_retotal: false })
        setResponses({ ...responses, force_retotal_success: true, force_retotal_error: null })
      }
    } catch (error) {
      setIsLoading({ ...loading, force_retotal: false })
      if (error?.response?.status === 404) {
        setResponses({ ...responses, force_retotal_error: 'Loan ID not found', force_retotal_success: null })
      } else {
        setResponses({ ...responses, force_retotal_error: error?.message, force_retotal_success: null })
      }
    }
  }

  const handleForcePaymentStatusChange = async () => {
    try {
      setIsLoading({ ...loading, force_change_payment_status: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/force_payment_status_change`)
      const response = await requests.post({ loan_id: loanForStatusChange, payment_id: paymentForStatusChange });
      if (response.status === 200) {
        setIsLoading({ ...loading, force_change_payment_status: false })
        setResponses({ ...responses, force_change_payment_status_success: true, force_change_payment_status_error: null })
      }
    } catch (error) {
      setIsLoading({ ...loading, force_change_payment_status: false })
      if (error?.response?.status === 404) {
        setResponses({ ...responses, force_change_payment_status_error: 'Loan ID not found', force_change_payment_status_success: null })
      } else {
        setResponses({ ...responses, force_change_payment_status_error: error?.response?.data?.message, force_change_payment_status_success: null })
      }
    }
  }

  const handleCreateSystemAdjustment = async () => {
    try {
      setIsLoading({ ...loading, create_system_adjustment: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/create_adjustment_payment`)
      const response = await requests.post({
        loan_id: loanForAdjustmentPayment,
        // The API prohibits these outright in production, so they must be omitted rather than sent as 0.
        ...(process.env.REACT_APP_ENVIRONMENT !== 'production' && {
          principal_amount: adjustmentPrincipal,
          interest_amount: adjustmentInterest,
          nsf_fee_amount: adjustmentNsfFee,
          late_fee_amount: adjustmentLateFee,
        }),
        prediscount_principal: adjustmentPrediscountPrincipal,
        prediscount_interest: adjustmentPrediscountInterest,
        prediscount_late_fee: adjustmentPrediscountLateFee,
        prediscount_nsf_fee: adjustmentPrediscountNsfFee,
      });
      if (response.status === 200) {
        setIsLoading({ ...loading, create_system_adjustment: false })
        setResponses({ ...responses, create_system_adjustment_success: true, create_system_adjustment_error: null })
      }
    } catch (error) {
      setIsLoading({ ...loading, create_system_adjustment: false })
      if (error?.response?.status === 404) {
        setResponses({ ...responses, create_system_adjustment_error: 'Loan ID not found', create_system_adjustment_success: null })
      } else {
        setResponses({ ...responses, create_system_adjustment_error: error?.response?.data?.message, create_system_adjustment_success: null })
      }
    }
  }

  const handleForcePaymentStatusChangeBulk = async () => {
    try {
      setIsLoading({ ...loading, force_change_payment_bulk_status: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/force_payment_status_change_bulk`);
      const response = await requests.post({ loan_ids: loansForStatusChangeBulk });
      if ([200, 207].includes(response.status)) {
        setIsLoading({ ...loading, force_change_payment_bulk_status: false })
        setResponses({
          ...responses,
          force_change_payment_bulk_status_success: response?.data?.success ?? {},
          force_change_payment_bulk_status_failed: response?.data?.failed ?? {},
        });
      }
    } catch (error) {
      setIsLoading({ ...loading, force_change_payment_bulk_status: false })
      setResponses({ ...responses, force_change_payment_bulk_status_error: error?.response?.data?.message })
    }
  }

  const handleForceReTotalLoanBulk = async () => {
    try {
      setIsLoading({ ...loading, force_re_total_loans_bulk: true })
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/admin_toolbox/force_re_total_loan_bulk`);
      const response = await requests.post({ loan_ids: loansForReTotalBulk });
      if ([200, 207].includes(response.status)) {
        setIsLoading({ ...loading, force_re_total_loans_bulk: false })
        setResponses({
          ...responses,
          force_re_total_loans_bulk_success: response?.data?.success ?? {},
          force_re_total_loans_bulk_failed: response?.data?.failed ?? {},
        });
      }
    } catch (error) {
      setIsLoading({ ...loading, force_re_total_loans_bulk: false })
      setResponses({ ...responses, force_re_total_loans_bulk_error: error?.response?.data?.message })
      setResponses({ ...responses, force_re_total_loans_bulk_success: {} })
      setResponses({ ...responses, force_re_total_loans_bulk_failed: {} })
    }
  }

  return (
    <LayoutSidebar
      contentTitle="Dev Toolbox"
      content={(
        <div>
          {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
            <>
              <p className="subtitle is-4 has-text-weight-bold mb-1">
                Feature Flags:
              </p>
              {loading.feature_flags && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
              {featureFlags.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <span style={{ width: '200px', fontWeight: 'bold' }}>Flag</span>
                  <span style={{ width: '100px', fontWeight: 'bold', textAlign: 'center' }}>Everyone</span>
                  <span style={{ width: '100px', fontWeight: 'bold', textAlign: 'center' }}>Just Me</span>
                </div>
              )}
              {featureFlags.map((flag) => (
                <div key={flag.class} className="mb-2" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ width: '200px' }}>{flag.name}</span>
                  <div style={{ width: '100px', display: 'flex', justifyContent: 'center' }}>
                    <button
                      type="button"
                      disabled={loading[`toggle_global_${flag.class}`]}
                      onClick={() => handleToggleFeatureFlag(flag.class, !flag.is_active_global, 'global')}
                      style={{
                        backgroundColor: flag.is_active_global ? '#48c78e' : '#f14668',
                        color: 'white',
                        border: 'none',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        minWidth: '60px',
                      }}
                    >
                      {flag.is_active_global ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <div style={{ width: '100px', display: 'flex', justifyContent: 'center', gap: '4px' }}>
                    {flag.has_user_override ? (
                      <>
                        <button
                          type="button"
                          disabled={loading[`toggle_user_${flag.class}`]}
                          onClick={() => handleToggleFeatureFlag(flag.class, !flag.is_active_user, 'user')}
                          style={{
                            backgroundColor: flag.is_active_user ? '#48c78e' : '#f14668',
                            color: 'white',
                            border: 'none',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            minWidth: '60px',
                          }}
                        >
                          {flag.is_active_user ? 'ON' : 'OFF'}
                        </button>
                        <button
                          type="button"
                          disabled={loading[`clear_${flag.class}`]}
                          onClick={() => handleClearUserOverride(flag.class)}
                          style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #ccc',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                          }}
                        >
                          Reset
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={loading[`toggle_user_${flag.class}`]}
                        onClick={() => handleToggleFeatureFlag(flag.class, !flag.is_active_global, 'user')}
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px dashed #ccc',
                          padding: '4px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          minWidth: '60px',
                          color: '#999',
                        }}
                      >
                        —
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="divider mt-4 mb-2"></div>

              <p className="subtitle is-4 has-text-weight-bold mb-1">
                DS Bypass Scenarios:
              </p>
              {loading.ds_bypass_scenarios && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
              {dsBypassScenarios.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <span style={{ width: '200px', fontWeight: 'bold' }}>Endpoint</span>
                  <span style={{ fontWeight: 'bold' }}>Active Scenario</span>
                </div>
              )}
              {dsBypassScenarios.map((row) => (
                <div key={row.endpoint} className="mb-2" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ width: '200px' }}>{row.endpoint}</span>
                  <select
                    value={row.active_scenario || ''}
                    disabled={loading[`ds_scenario_${row.endpoint}`]}
                    onChange={(e) => handleActivateDsScenario(row.endpoint, e.target.value)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      minWidth: '200px',
                    }}
                  >
                    {row.scenarios.map((scenario) => (
                      <option key={scenario.id} value={scenario.scenario_name}>
                        {scenario.scenario_name}
                      </option>
                    ))}
                  </select>
                  {loading[`ds_scenario_${row.endpoint}`] && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
                </div>
              ))}
              <div className="divider mt-4 mb-2"></div>
            </>
          )}
          <p className="subtitle is-4 has-text-weight-bold mb-1">
            Artisan:

          </p>
          <div className="mb-2">
            <button type="button" disabled={loading.cache} onClick={handleCacheClear}>Clear Cache</button>
            {loading.cache && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
            {responses.cache_success && <span className="pl-3">Successfully cleared cache</span>}
            {responses.cache_error &&
              <span className="pl-3 has-text-danger lox-capitalize-first-letter">Error: {responses.cache_error}</span>}
          </div>
          <div className="mb-2">
            <button type="button" disabled={loading.config} onClick={handleConfigClear}>Clear Config</button>
            {loading.config && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
            {responses.config_success && <span className="pl-3">Successfully cleared config</span>}
            {responses.config_error &&
              <span className="pl-3 has-text-danger lox-capitalize-first-letter">Error: {responses.config_error}</span>}
          </div>
          {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
            <>
              <div className="divider mt-4 mb-2"></div>
              <p className="subtitle is-4 has-text-weight-bold mb-1">
                Loan:
              </p>
              <p className="is-5">Sign loan agreement as customer:</p>
              <div>
                <input
                  type="text"
                  value={loanAgreementForSigning}
                  placeholder="lead ID (Not guid)"
                  onChange={(e) => setLoanAgreementForSigning(e.target.value)}
                />
                <button type="button" disabled={loading.sign_loan_agreement} onClick={handleSignLoanAsCustomer}>Sign Loan Agreement</button>
                {loading.sign_loan_agreement && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
                {responses.sign_loan_agreement_success && <span className="pl-3">{responses.sign_loan_agreement_message}</span>}
                {responses.sign_loan_agreement_error && (
                  <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                    Error: {responses.sign_loan_agreement_error}
                  </span>
                )}
              </div>
              <p className="is-5 mt-2">Reset loan to original state:</p>
              <div>
                <input
                  type="text"
                  value={loanForReset}
                  placeholder="loan ID (Not guid)"
                  onChange={(e) => setLoanForReset(e.target.value)}
                />
                <button type="button" disabled={loading.reset_loan} onClick={handleResetLoan}>Reset Loan</button>
                {loading.reset_loan && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
                {responses.reset_loan_success && <span className="pl-3">{responses.reset_loan_message}</span>}
                {responses.reset_loan_error && (
                  <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                    Error: {responses.reset_loan_error}
                  </span>
                )}
              </div>
            </>
          )}
          <div className="divider mt-4 mb-2"></div>
          <p className="subtitle is-4 has-text-weight-bold mb-1">
            Payment Schedule:
          </p>
          <p className="is-5">Force loan recalculation:</p>
          <div>
            <input
              type="text"
              value={loanForRecalc}
              placeholder="loan ID (Not guid)"
              onChange={(e) => setLoanForRecalc(e.target.value)}
            />
            <button type="button" disabled={loading.force_recalc} onClick={handleForceRecalculation}>Force Recalc
            </button>
            {loading.force_recalc && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
            {responses.force_recalc_success && <span className="pl-3">Successfully recalculated loan</span>}
            {responses.force_recalc_error && (
              <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                Error: {responses.force_recalc_error}
              </span>
            )}
          </div>
          <p className="is-5 mt-2">Force Loan Retotal:</p>
          <div>
            <input
              type="text"
              value={loanForRetotal}
              placeholder="loan ID (Not guid)"
              onChange={(e) => setLoanForRetotal(e.target.value)}
            />
            <button type="button" disabled={loading.force_retotal} onClick={handleForceRetotal}>Force Retotal
            </button>
            {loading.force_retotal && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
            {responses.force_retotal_success && <span className="pl-3">Successfully retotaled loan</span>}
            {responses.force_retotal_error && (
              <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                Error: {responses.force_retotal_error}
              </span>
            )}
          </div>
          <p className="is-5 mt-2">Force Re-Run Change Payment Status:</p>
          <div>
            <input
              type="text"
              value={loanForStatusChange}
              placeholder="loan ID (Not guid)"
              onChange={(e) => setLoanForStatusChange(e.target.value)}
            />
            <input
              type="text"
              value={paymentForStatusChange}
              placeholder="payment ID (Not guid)"
              onChange={(e) => setPaymentForStatusChange(e.target.value)}
            />
            <button
              type="button"
              disabled={loading.force_change_payment_status}
              onClick={handleForcePaymentStatusChange}
            >Force Re-run Status Change
            </button>
            {loading.force_change_payment_status && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
            {responses.force_change_payment_status_success &&
              <span className="pl-3">Successfully re-ran change payment status</span>}
            {responses.force_change_payment_status_error && (
              <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                Error: {responses.force_change_payment_status_error}
              </span>
            )}
          </div>

          <p className="is-5 mt-2">Create System Adjustment Payment:</p>
          <div>
            <div style={{ display: 'flex' }}>
              <div className="mr-5">
                <p className="has-text-grey is-small is-size-7">
                  <span className="mr-1">Loan ID</span>
                </p>
                <input
                  type="number"
                  value={loanForAdjustmentPayment}
                  onChange={(e) => setLoanForAdjustmentPayment(e.target.value)}
                  step="0.01"
                />
              </div>
            </div>
            {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
              <div style={{ display: 'flex' }}>
                <div className="mr-5">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Principal Amount</span>
                  </p>
                  <input
                    type="number"
                    value={adjustmentPrincipal}
                    onChange={(e) => setAdjustmentPrincipal(e.target.value)}
                    step="0.01"
                  />
                </div>
                <div className="mr-5">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Interest Amount</span>
                  </p>
                  <input
                    type="number"
                    value={adjustmentInterest}
                    onChange={(e) => setAdjustmentInterest(e.target.value)}
                    step="0.01"
                  />
                </div>
                <div className="mr-5">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Late Fee Amount</span>
                  </p>
                  <input
                    type="number"
                    value={adjustmentLateFee}
                    onChange={(e) => setAdjustmentLateFee(e.target.value)}
                    step="0.01"
                  />
                </div>
                <div className="mr-5">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">NSF Fee Amount</span>
                  </p>
                  <input
                    type="number"
                    value={adjustmentNsfFee}
                    onChange={(e) => setAdjustmentNsfFee(e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>
            )}
            <div style={{ display: 'flex' }}>
              <div className="mr-5">
                <p className="has-text-grey is-small is-size-7">
                  <span className="mr-1">Prediscount Principal Amount</span>
                </p>
                <input
                  type="number"
                  value={adjustmentPrediscountPrincipal}
                  onChange={(e) => setAdjustmentPrediscountPrincipal(e.target.value)}
                  step="0.01"
                />
              </div>
              <div className="mr-5">
                <p className="has-text-grey is-small is-size-7">
                  <span className="mr-1">Prediscount Interest Amount</span>
                </p>
                <input
                  type="number"
                  value={adjustmentPrediscountInterest}
                  onChange={(e) => setAdjustmentPrediscountInterest(e.target.value)}
                  step="0.01"
                />
              </div>
              <div className="mr-5">
                <p className="has-text-grey is-small is-size-7">
                  <span className="mr-1">Prediscount Late Fee Amount</span>
                </p>
                <input
                  type="number"
                  value={adjustmentPrediscountLateFee}
                  onChange={(e) => setAdjustmentPrediscountLateFee(e.target.value)}
                  step="0.01"
                />
              </div>
              <div className="mr-5">
                <p className="has-text-grey is-small is-size-7">
                  <span className="mr-1">Prediscount NSF Fee Amount</span>
                </p>
                <input
                  type="number"
                  value={adjustmentPrediscountNsfFee}
                  onChange={(e) => setAdjustmentPrediscountNsfFee(e.target.value)}
                  step="0.01"
                />
              </div>
            </div>
            <button
              type="button"
              disabled={loading.create_system_adjustment}
              onClick={handleCreateSystemAdjustment}
            >Create System Adjustment
            </button>
            {loading.create_system_adjustment && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
            {responses.create_system_adjustment_success &&
              <span className="pl-3">Successfully Created System Adjustment</span>}
            {responses.create_system_adjustment_error && (
              <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                Error: {responses.create_system_adjustment_error}
              </span>
            )}
          </div>

          <p className="is-5 mt-2">Force Re-Run Change Payment Status Bulk:</p>
          <div>
            <div>
              <textarea
                style={{
                  padding: '2px',
                  transition: 'height 0.3s ease-in-out',
                  width: '308px'
                }}
                onFocus={(e) => e.target.style.height = '200px'}
                onBlur={(e) => e.target.style.height = '22px'}
                value={loansForStatusChangeBulk}
                placeholder="loan IDs (Not guids)"
                onChange={(e) => {
                  setLoansForStatusChangeBulk(e.target.value);
                  if (/^\s*(\d+\s*,\s*)*\d+\s*,?\s*$/.test(e.target.value)) {
                    setError('');
                  } else {
                    setError('Please enter numbers separated by commas.');
                  }
                }}
              />
              {error && <p className="has-text-danger">{error}</p>}
            </div>
            <div>
              <button
                type="button"
                disabled={loading.force_change_payment_bulk_status}
                onClick={handleForcePaymentStatusChangeBulk}
              >Force Re-run Status Change Bulk Update
              </button>
              {loading.force_change_payment_bulk_status &&
                <div className="is-inline-block"><Loading size="is-size-7" /></div>}
              {responses.force_change_payment_bulk_status_error && (
                <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                Error: {responses.force_change_payment_bulk_status_error}
              </span>
              )}
            </div>
            <div>
              <div>
                {responses.force_change_payment_bulk_status_success && Object.keys(responses.force_change_payment_bulk_status_success).length > 0 && (
                  <span
                    className="mt-2"
                  >Successfully re-ran change payment status for the following loans: {Object.keys(responses.force_change_payment_bulk_status_success).join(', ')}</span>
                )}
              </div>
              <div>
                {responses.force_change_payment_bulk_status_failed && Object.keys(responses.force_change_payment_bulk_status_failed).length > 0 && (
                  <div className="has-text-danger lox-capitalize-first-letter">
                    <p>Errors on the following loans:</p>
                    {Object.entries(responses.force_change_payment_bulk_status_failed).map(([key, value]) => (
                      <p key={key}>{`${key}: ${value}`}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className="is-5 mt-2">Force Re-Total Loan In Bulk:</p>
          <div>
            <div>
              <textarea
                style={{
                  padding: '2px',
                  transition: 'height 0.3s ease-in-out',
                  width: '308px'
                }}
                onFocus={(e) => e.target.style.height = '200px'}
                onBlur={(e) => e.target.style.height = '50px'}
                value={loansForReTotalBulk}
                placeholder="loan IDs (Not guids)"
                onChange={(e) => {
                  setLoansForReTotalBulk(e.target.value);
                  if (/^\s*(\d+\s*,\s*)*\d+\s*,?\s*$/.test(e.target.value)) {
                    setErrorReTotalBulk('');
                  } else {
                    setErrorReTotalBulk('Please enter numbers separated by commas.');
                  }
                }}
              />
              {errorReTotalBulk && <p className="has-text-danger">{errorReTotalBulk}</p>}
            </div>
            <div>
              <button
                type="button"
                disabled={loading.force_re_total_loans_bulk || errorReTotalBulk}
                onClick={handleForceReTotalLoanBulk}
              >Force Re-run Total Loans In Bulk
              </button>
              {loading.force_re_total_loans_bulk &&
                <div className="is-inline-block"><Loading size="is-size-7" /></div>}
              {responses.force_re_total_loans_bulk_error && (
                <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                Error: {responses.force_re_total_loans_bulk_error}
              </span>
              )}
            </div>
            <div>
              <div>
                {responses.force_re_total_loans_bulk_success && Object.keys(responses.force_re_total_loans_bulk_success).length > 0 && (
                  <span
                    className="mt-2"
                  >Successfully re-ran the total for following loans: {Object.keys(responses.force_re_total_loans_bulk_success).join(', ')}</span>
                )}
              </div>
              <div>
                {responses.force_re_total_loans_bulk_failed && Object.keys(responses.force_re_total_loans_bulk_failed).length > 0 && (
                  <div className="has-text-danger lox-capitalize-first-letter">
                    <p>Errors on the following loans:</p>
                    {Object.entries(responses.force_re_total_loans_bulk_failed).map(([key, value]) => (
                      <p key={key}>{`${key}: ${value}`}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="divider mt-4 mb-2"></div>
          <p className="subtitle is-4 has-text-weight-bold mb-1">
            Crons:
          </p>
          <p className="is-5 mt-2">Run cron:lead_status_pending_approval </p>
          <div>
            <button
              type="button"
              disabled={loading.lead_status_pending_cron}
              onClick={handleLeadStatusPendingApprovalCron}
            >
              Run Cron
            </button>
            {loading.lead_status_pending_cron && <div className="is-inline-block"><Loading size="is-size-7" /></div>}
            {responses.lead_status_pending_cron_success &&
              <span className="pl-3">Successfully queued cron</span>}
            {responses.lead_status_pending_cron_error && (
              <span className="pl-3 has-text-danger lox-capitalize-first-letter">
                Error: {responses.lead_status_pending_cron_error}
              </span>
            )}
          </div>
        </div>
      )}
    />
  )
}

export default DevToolbox;
